export const SUPPORT_ATTACHMENT_RULES = {
    maxFiles: 2,
    maxSizeMb: 4,
    allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    accept: 'image/png,image/jpeg,image/webp'
} as const

const MAX_PREVIEW_DIMENSION = 480
const MAX_PREVIEW_DATA_URL_LENGTH = 120_000
const PREVIEW_QUALITY = 0.72

export interface SupportAttachment {
    id: string
    name: string
    type: string
    size: number
    previewDataUrl: string | null
}

const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () =>
            reject(new Error(`No se pudo leer el archivo ${file.name}.`))
        reader.readAsDataURL(file)
    })

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image()

        image.onload = () => resolve(image)
        image.onerror = () =>
            reject(new Error('No se pudo procesar la imagen seleccionada.'))
        image.src = dataUrl
    })

const buildPreviewDataUrl = async (file: File): Promise<string | null> => {
    const originalDataUrl = await readFileAsDataUrl(file)

    if (!file.type.startsWith('image/')) {
        return null
    }

    const image = await loadImage(originalDataUrl)
    const scale = Math.min(
        1,
        MAX_PREVIEW_DIMENSION / Math.max(image.width, image.height)
    )
    const width = Math.max(1, Math.round(image.width * scale))
    const height = Math.max(1, Math.round(image.height * scale))
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
        return null
    }

    canvas.width = width
    canvas.height = height
    context.fillStyle = '#FFFFFF'
    context.fillRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    const previewDataUrl = canvas.toDataURL('image/jpeg', PREVIEW_QUALITY)

    return previewDataUrl.length <= MAX_PREVIEW_DATA_URL_LENGTH
        ? previewDataUrl
        : null
}

export const getSupportAttachmentError = (
    file: File,
    maxSizeMb: number = SUPPORT_ATTACHMENT_RULES.maxSizeMb
): string | null => {
    if (!SUPPORT_ATTACHMENT_RULES.allowedMimeTypes.includes(file.type)) {
        return `El archivo ${file.name} debe ser PNG, JPG o WEBP.`
    }

    if (file.size > maxSizeMb * 1024 * 1024) {
        return `El archivo ${file.name} supera el maximo de ${maxSizeMb} MB.`
    }

    return null
}

export const formatSupportAttachmentSize = (sizeInBytes: number): string => {
    if (sizeInBytes < 1024 * 1024) {
        return `${Math.max(1, Math.round(sizeInBytes / 1024))} KB`
    }

    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`
}

export const createSupportAttachment = async (
    file: File
): Promise<SupportAttachment> => ({
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
    name: file.name,
    type: file.type,
    size: file.size,
    previewDataUrl: await buildPreviewDataUrl(file)
})
